import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { Icon } from "../../../components";
import { useTheme } from "../../../hooks";
import { BaseInputProps } from "../../../types/InputTypes";

type InputEditToggleProps = {
  hasEditControls?: boolean;
  startReadOnly?: boolean;
  saveIcon?: string;
  readOnlyIcon?: string;
  cancelIcon?: string;
  input: React.ReactElement<BaseInputProps>;
};

const InputEditToggle: React.FC<React.PropsWithChildren<InputEditToggleProps>> = ({
  hasEditControls = true,
  startReadOnly = true,
  input,
  saveIcon = "fa-circle-check",
  cancelIcon = "fa-circle-xmark",
  readOnlyIcon = "fa-pencil-alt",
}) => {
  const { Theme } = useTheme();
  const [isReadOnly, setIsReadOnly] = useState<boolean>(startReadOnly);
  const [localValue, setLocalValue] = useState<any | undefined>(undefined);

  const updateValue = () => {
    const onChange = input.props.onChange;
    if (!!onChange) {
      onChange(localValue);
    }
  };

  const toggle = () => {
    setIsReadOnly(!isReadOnly);
  };

  const save = () => {
    toggle();
    updateValue();
  };

  const close = () => {
    toggle();
    setLocalValue(input.props.value);
  };

  useEffect(() => {
    if (!!input) {
      setLocalValue(input.props.value);
    } else {
      setLocalValue(undefined);
    }
  }, [input]);

  return hasEditControls ? (
    <div className={clsx("d-flex align-items-start", { "mb-2": input.props.gutterBottom, "flex-column": !isReadOnly })}>
      {isReadOnly && (
        <Icon
          className={clsx("me-3")}
          iconName={readOnlyIcon}
          onClick={toggle}
          styles={{
            ...Theme.icon,
            size: ".75rem",
          }}
        />
      )}
      <div className="w-100 h-100">
        {React.cloneElement(input, {
          ...input.props,
          gutterBottom: false,
          readOnly: isReadOnly,
          styles: {
            ...input.props?.styles,
            label: {
              marginBottom: 4,
              ...input.props?.styles?.label,
            },
          },
          value: localValue,
          onChange: (value: any) => {
            setLocalValue(value);
          },
          onClick: () => {
            if (isReadOnly) {
              toggle();
            }
          },
        })}
      </div>
      {!isReadOnly && (
        <div className="mt-2">
          <Icon
            className="me-2"
            iconName={saveIcon}
            onClick={save}
            styles={{
              ...Theme.icon,
              size: "1.1rem",
              color: Theme.colors.green,
            }}
          />
          <Icon
            iconName={cancelIcon}
            onClick={close}
            styles={{
              ...Theme.icon,
              size: "1.1rem",
              color: Theme.colors["mlb-red"],
            }}
          />
        </div>
      )}
    </div>
  ) : (
    <>{input}</>
  );
};

export default InputEditToggle;
